import { useEffect, useRef, useState } from 'preact/hooks'
import { audio, audioSettings as pageAudio } from '../../audio'
import type { AudioSettings, AudioSettingsStore } from '../../audio/settings'
import type { Session } from '../session'
import { useStore } from '../useStore'
import { ControlsHelp } from './ControlsHelp'
import { fxSettings } from './fxSettings'
import type { FxSettingsStore } from './fxSettings'
import styles from './settings.module.css'

type Tab = 'general' | 'controls'
type Volume = 'master' | 'music' | 'sfx'

const VOLUMES: readonly { key: Volume; label: string }[] = [
  { key: 'master', label: 'Master' },
  { key: 'music', label: 'Music' },
  { key: 'sfx', label: 'Sound effects' },
]

/** The release shown at the bottom (`VITE_RELEASE`: a commit or `dev`). */
export function versionLine(release: string | undefined): string {
  return `Runkr ${release || 'dev'}`
}

function Slider({
  id,
  label,
  value,
  onValue,
  onDone,
}: {
  id: string
  label: string
  value: number
  onValue: (v: number) => void
  onDone: () => void
}) {
  const pct = Math.round(value * 100)
  const read = (e: Event): number => Number((e.currentTarget as HTMLInputElement).value) / 100
  return (
    <div class={styles.sliderRow}>
      <label for={id} class={styles.label}>
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={0}
        max={100}
        step={5}
        value={pct}
        class={styles.slider}
        style={{ '--fill': `${pct}%` }}
        aria-valuetext={`${pct} percent`}
        onInput={(e) => onValue(read(e))}
        onChange={onDone}
        data-testid={`volume-${id}`}
      />
      <span class={styles.value} aria-hidden="true">
        {pct}
      </span>
    </div>
  )
}

function Switch({
  label,
  hint,
  on,
  onToggle,
  testId,
}: {
  label: string
  hint?: string
  on: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      class={styles.switchRow}
      onClick={onToggle}
      data-testid={testId}
    >
      <span class={styles.switchText}>
        <span class={styles.label}>{label}</span>
        {hint && <span class={styles.hint}>{hint}</span>}
      </span>
      <span class={`${styles.switch} ${on ? styles.switchOn : ''}`} aria-hidden="true" />
    </button>
  )
}

interface Props {
  session: Pick<Session, 'toMenu'>
  /** Injected by tests; the page's stores otherwise. */
  audioStore?: AudioSettingsStore
  fx?: FxSettingsStore
}

/**
 * Settings (PHASE3 T3.7, flow screen `settings`, opened from the hub only): Master / Music /
 * Sound effects sliders and Mute bound to the T3.6 audio store, Reduce effects (`runkr.fx`),
 * Language (English only for now), the version line, and the Controls help on its own tab.
 * Back or Escape returns to the hub. Fits 740×360 without page scroll.
 */
export function SettingsScreen({ session, audioStore = pageAudio, fx = fxSettings() }: Props) {
  const sound: AudioSettings = useStore(audioStore.store)
  const effects = useStore(fx.store)
  const [tab, setTab] = useState<Tab>('general')
  const back = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    back.current?.focus()
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      session.toMenu()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [session])

  return (
    <div class={styles.page}>
      <header class={styles.header}>
        <button ref={back} type="button" class={styles.back} onClick={() => session.toMenu()}>
          Back
        </button>
        <h1 class={styles.title}>Settings</h1>
        <div class={styles.tabs} role="tablist" aria-label="Settings">
          {(['general', 'controls'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              class={`${styles.tab} ${tab === t ? styles.tabOn : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'general' ? 'Sound & display' : 'Controls'}
            </button>
          ))}
        </div>
      </header>
      {tab === 'controls' ? (
        <main class={styles.body}>
          <ControlsHelp />
        </main>
      ) : (
        <main class={`${styles.body} ${styles.columns}`}>
          <section class={styles.panel} aria-label="Sound">
            <h2 class={styles.sectionTitle}>Sound</h2>
            {VOLUMES.map((v) => (
              <Slider
                key={v.key}
                id={v.key}
                label={v.label}
                value={sound[v.key]}
                onValue={(n) => audioStore.set({ [v.key]: n })}
                onDone={() => v.key !== 'music' && audio.play('click')}
              />
            ))}
            <Switch
              label="Mute"
              hint="M also mutes, anywhere"
              on={sound.muted}
              onToggle={() => audioStore.toggleMute()}
              testId="mute"
            />
          </section>
          <section class={styles.panel} aria-label="Display">
            <h2 class={styles.sectionTitle}>Display</h2>
            <Switch
              label="Reduce effects"
              hint="No confetti or shake, fewer particles. From the next race."
              on={!effects}
              onToggle={() => fx.set(!effects)}
              testId="reduce-effects"
            />
            <div class={styles.languageRow}>
              <label for="language" class={styles.label}>
                Language
              </label>
              <select id="language" class={styles.select} disabled>
                <option>English</option>
              </select>
              <span class={styles.hint}>More soon</span>
            </div>
            <p class={styles.version}>{versionLine(import.meta.env.VITE_RELEASE)}</p>
          </section>
        </main>
      )}
    </div>
  )
}
